// Contexto de autenticacao e o hook que o le.
//
// Fica separado do `AuthProvider` porque um arquivo que exporta componente e
// nao-componente ao mesmo tempo quebra o Fast Refresh do Vite: a cada edicao o
// modulo inteiro remonta e a sessao em memoria se perde.

import { createContext, useContext } from 'react';

export type UserRole = 'superuser' | 'admin' | 'cliente' | null;

export interface AuthUser {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
  avatar?: string;
  /** Caminho no bucket. `avatar` guarda a URL assinada, que expira. */
  avatarPath?: string;
  tipo?: 'pf' | 'pj';
  accountId?: string;
  /**
   * Quando o dono do e-mail provou posse. `undefined` = pendente (ADR-0014).
   *
   * Vem de `profiles.email_verificado_em`, e **não** de
   * `auth.users.email_confirmed_at`: com `MAILER_AUTOCONFIRM=true` aquele campo
   * é sempre preenchido e deixou de carregar informação.
   */
  emailVerificadoEm?: string;
}

export interface ImpersonatedAccount {
  accountId: string;
  nome: string;
  email: string;
}

export interface ProfileRow {
  id: string;
  tipo: 'pf' | 'pj';
  nome: string | null;
  razao_social: string | null;
  email: string | null;
  account_id: string | null;
  superusuario?: boolean | null;
  avatar_url?: string | null;
  email_verificado_em?: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (data: {
    nome: string;
    email: string;
    /** Caminho do arquivo no bucket `avatares`. Nulo remove a foto. */
    avatarPath?: string | null;
  }) => Promise<{ error?: string }>;
  changePassword: (senhaAtual: string, novaSenha: string) => Promise<{ error?: string }>;
  isLoggedIn: boolean;
  isSuperuser: boolean;
  loading: boolean;
  effectiveAccountId: string | undefined;
  impersonatedAccount: ImpersonatedAccount | null;
  setImpersonatedAccount: (account: ImpersonatedAccount | null) => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  login: async () => ({ error: 'AuthProvider not mounted' }),
  logout: async () => {},
  updateProfile: async () => ({ error: 'AuthProvider not mounted' }),
  changePassword: async () => ({ error: 'AuthProvider not mounted' }),
  isLoggedIn: false,
  isSuperuser: false,
  loading: true,
  effectiveAccountId: undefined,
  impersonatedAccount: null,
  setImpersonatedAccount: () => {},
});

export function mapProfileToUser(
  sessionUser: { id: string; email?: string },
  profile: ProfileRow | null,
): AuthUser | null {
  if (!sessionUser.email) return null;

  const displayName =
    profile?.tipo === 'pj'
      ? profile.razao_social || profile.nome || sessionUser.email.split('@')[0]
      : profile?.nome || sessionUser.email.split('@')[0];

  // O papel vem do banco, nao de uma constante de e-mail: e a mesma coluna que
  // as policies de RLS consultam (migration 0006). Sem ela, a interface
  // liberaria telas que o banco recusaria.
  const role: UserRole = profile?.superusuario ? 'superuser' : 'cliente';

  return {
    id: sessionUser.id,
    email: sessionUser.email,
    nome: displayName,
    role,
    tipo: profile?.tipo,
    accountId: profile?.account_id || undefined,
    // Guarda o caminho; a URL assinada e resolvida por quem carrega o perfil.
    avatarPath: profile?.avatar_url || undefined,
    emailVerificadoEm: profile?.email_verificado_em || undefined,
  };
}

export const IMPERSONATION_STORAGE_KEY = 'cc_impersonated_account';

export function useAuth() {
  return useContext(AuthContext);
}
